import { type GrowthBookSourceAttributes } from '@doxyme/config';
import { storage } from '@doxyme/ui/utils';
import { store } from '../../stores/redux/store';
import {
  getInstitutionsMembershipsIds,
  getInstitutionId,
} from '../../features/apolloSession/apolloSession.selectors';
import { UserPlanType, getUserPlanType } from '../../hooks/useUserSubscription';
import { getSubscriptionType } from '../../services';
import { StorageKeys } from '../constants';

/**
 * Returns growthbook attributes, based on which we get feature values
 *
 * Example return:
 * ```ts
 * { id: userId, isProUser: true, ..., institutionId: 'someId', etc }
 * ```
 * @param _store - Internal parameter for Dependency Injection [DO NOT USE] - store
 */
export const getGrowthBookAttributes = async (
  _store = store,
): Promise<GrowthBookSourceAttributes> => {
  const state = _store.getState();

  // userId must be a number from storage
  const userId =
    storage.findItem(StorageKeys.CurrentUserId) === undefined
      ? ''
      : `${storage.findItem(StorageKeys.CurrentUserId) as number}`;

  let planType = undefined;

  if (Boolean(userId)) {
    const { data } = await _store.dispatch(
      getSubscriptionType.initiate({
        userId: String(userId),
      }),
    );
    planType = getUserPlanType(data?.subscriptionType);
  }

  return {
    ..._getPlanAttributes(planType),
    id: userId,
    institutionsMembershipsIds: getInstitutionsMembershipsIds(state),
    institutionId: getInstitutionId(state),
  };
};

/**
 * Internal function. Get part of attributes related to the plan
 */
export const _getPlanAttributes = (
  planType?: UserPlanType,
): Pick<
  GrowthBookSourceAttributes,
  'isClinicUser' | 'isFreeUser' | 'isProUser'
> => {
  return {
    isFreeUser: planType === UserPlanType.Free,
    isProUser: planType === UserPlanType.Professional,
    isClinicUser: planType === UserPlanType.Clinic,
  };
};
